// @ts-strict-ignore
import {computed, Directive, ElementRef, inject, input, OnDestroy, OnInit, output, signal, viewChild} from "@angular/core";
import {FormControl} from "@angular/forms";
import {AutoCompleteResult, BasicSearchParams, FetchDataType, SearchFilter, SearchParameters} from "@models";
import {distinctUntilChanged, from} from "rxjs";
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {debounceTime, filter, mergeMap, startWith} from "rxjs/operators";
import {AutocompleteService, FetchData_TypeMapping} from "./autocomplete.service";
import {Translations} from "@translations";
import {MatAutocomplete, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {derivedAsync} from "ngxtension/derived-async";

@UntilDestroy()
@Directive()
export abstract class BaseAutocompleteDirective<T extends keyof FetchData_TypeMapping> implements OnInit, OnDestroy {
	// I/O
	public readonly dataType = input.required<FetchDataType>();
	public readonly labelToken = input.required<string>();
	public readonly formCtrl = input<FormControl<FetchData_TypeMapping[T]> | null>(null);
	public readonly pageSize = input<number>(25);
	public excludeIds = input<string[]>([]);
	public searchFilters = input<SearchFilter[], SearchFilter | SearchFilter[]>([], {
		transform: (value: SearchFilter | SearchFilter[]) => (Array.isArray(value) ? value : [value]),
	});
	onLoad = output<void>();
	onSelect = output<FetchData_TypeMapping[T]>();

	// View Children
	public searchInput = viewChild.required<ElementRef<HTMLInputElement>>("searchInput");
	public autocomplete = viewChild.required<MatAutocomplete>("auto");
	public trigger = viewChild.required<MatAutocompleteTrigger>("trigger");

	// Properties
	public readonly filteredOptions = signal<AutoCompleteResult[]>([]);
	public selectedOption: AutoCompleteResult | null = null;
	public separatorKeysCodes: number[] = [ENTER, COMMA];
	public readonly inputCtrl: FormControl<string | AutoCompleteResult> = new FormControl<string | AutoCompleteResult>("");
	readonly #isLoading = signal<boolean>(false);
	readonly #searchCallback: () => void = () => this.inputCtrl.setValue("");

	// Derived properties
	public readonly showSpinner = derivedAsync<boolean>(() => this.computeShowSpinner(this.#isLoading(), this.filteredOptions()));
	readonly #searchFilters = computed<SearchFilter[]>(() => this._computeSearchFilters());

	// Readonly
	readonly T = Translations;

	// Injections
	readonly autocompleteService = inject(AutocompleteService);

	public ngOnInit(): void {
		// Subscriptions
		this.observeSearchChanges();
		this.observeFormChanges();

		// Add focus eventListener -> trigger search on focus
		this.searchInput().nativeElement.addEventListener("focus", this.#searchCallback);
	}

	public ngOnDestroy(): void {
		// Clean up
		this.searchInput().nativeElement.removeEventListener("focus", this.#searchCallback);
	}

	/**
	 * Handle option selection. Used both for user input and automatic behaviors, such as default selection.
	 *
	 * @param selectOption an item to be select
	 * @param options emitEvent - whether to emit the formControl event. Defaults to true
	 */
	public handleSelect(selectOption: AutoCompleteResult, options?: {emitEvent: boolean}): void {
		const emitEvent = options?.emitEvent !== false;
		this.inputCtrl.reset("", {emitEvent: false});
		if (selectOption?.id) {
			this.selectedOption = selectOption;
			from(this.autocompleteService.fetchSingle<T>(FetchDataType[FetchDataType[this.dataType()]], selectOption.id))
				.pipe(untilDestroyed(this), debounceTime(1000))
				.subscribe((result) => {
					this.formCtrl()?.patchValue(result, {emitEvent});
					if (emitEvent) {
						this.onSelect.emit(result);
					}
				});
		} else {
			this.formCtrl()?.patchValue(null, {emitEvent});
			if (emitEvent) {
				this.onSelect.emit(null);
			}
		}
	}

	public removeSelection(): void {
		this.selectedOption = null;
		this.formCtrl()?.reset();
		this.inputCtrl.setValue("");
		this.searchInput().nativeElement.value = "";
	}

	protected onSearchButtonClick(): void {
		if (this.autocomplete().isOpen) {
			this.trigger().closePanel();
		} else {
			this.inputCtrl.setValue("");
			this.trigger().openPanel();
			setTimeout(() => {
				this.searchInput().nativeElement.focus();
				this.searchInput().nativeElement.select();
			}, 100);
		}
	}

	/**
	 * Observe the search textInput, fetch values from the server that fit the search criteria
	 * @private
	 */
	private observeSearchChanges(): void {
		this.inputCtrl.valueChanges
			.pipe(
				untilDestroyed(this),
				debounceTime(300),
				filter(
					// Prevent selection triggering search
					(searchString): searchString is string => typeof searchString === "string",
				),
				mergeMap((searchString) => {
					// Build params
					const textFilter: SearchFilter = {
						searchTerms: [searchString],
						searchColumn: AutocompleteService.getColumnForDataType(this.dataType()),
					};
					const searchParams = {
						...new BasicSearchParams(this.pageSize()),
						sortColumn: AutocompleteService.getColumnForDataType(this.dataType()),
						searchFilters: [textFilter, ...this.#searchFilters()],
					} as SearchParameters;

					// Fetch options
					this.#isLoading.set(true);
					return from(this.autocompleteService.fetchData(this.dataType(), searchParams));
				}),
			)
			.subscribe((filteredOptions) => {
				this.filteredOptions.set(filteredOptions);
				this.#isLoading.set(false);
			});
	}

	/**
	 * React to changes coming to the formControl from the outside. E.g. if a parent sets it a default value
	 * @private
	 */
	private observeFormChanges() {
		this.formCtrl()
			?.valueChanges.pipe(
				untilDestroyed(this),
				startWith(this.formCtrl()?.value),
				filter(() => this.formCtrl().valid || this.formCtrl().pristine),
				filter((value) => !Array.isArray(value)), // For some reason the value is sometimes an array. Seems to be an Angular bug
				filter(() => this.formCtrl().value?.id !== this.selectedOption?.id),
				distinctUntilChanged(
					(valueA: FetchData_TypeMapping[T] | null, valueB: FetchData_TypeMapping[T] | null) => valueA?.id === valueB?.id,
				),
				debounceTime(300),
			)
			.subscribe(() => {
				// Validate
				const selectedId = this.formCtrl().getRawValue()?.id;
				if (!selectedId) {
					this.selectedOption = null;
					this.onLoad.emit();
					return;
				}

				// Fetch
				this.autocompleteService
					.fetchData(this.dataType(), {
						...new BasicSearchParams(),
						sortColumn: AutocompleteService.getColumnForDataType(this.dataType()),
						searchFilters: [{searchTerms: [selectedId], searchColumn: "id"} as SearchFilter],
					})
					.then((result) => {
						const [defaultResult] = result;
						this.handleSelect(defaultResult, {emitEvent: false});
						this.onLoad.emit();
					});
			});
	}

	/**
	 * Combine input searchFilters with excludeIds.
	 * @private
	 */
	private _computeSearchFilters(): SearchFilter[] {
		const result: SearchFilter[] = [...this.searchFilters()];
		if (!!this.excludeIds().length) {
			result.push(AutocompleteService.getSearchFilter(this.excludeIds(), {searchColumn: "id", negate: true}));
		}
		return result;
	}

	private computeShowSpinner(isLoading: boolean, filteredOptions: AutoCompleteResult[]) {
		if (!isLoading) {
			return false;
		}

		// Show loading on initial load
		if (!filteredOptions.length) {
			return true;
		}

		// Show follow-up loading if it takes a noticeable amount of time
		setTimeout(() => {
			if (isLoading) {
				return true;
			}
		}, 100);
	}
}
